import { useFeatureFlag } from 'src/integrations/feature-flags/feature-flags';

/**
 * Made-up release, PO and transfer numbers for demoing the order-number
 * columns before we ingest the ERP's acknowledgements (GEN-3143). Nothing
 * stores them and none exists in AS400. Each is derived from the order's id,
 * so a row keeps its numbers across reloads and pages.
 *
 * The shapes copy what buyers see in GAINS: a 7-digit release (tracking)
 * number like `3747693`, a 5-digit PO number printed with its leading zero,
 * and 1 to 3 consecutive 7-digit transfer numbers, because the DC's zone
 * printing splits one release into several transfers.
 */

/** FNV-1a over the salted id, so each number is drawn independently. */
function hash(orderId: string, salt: string): number {
  const key = `${salt}:${orderId}`;
  let h = 0x811c9dc5;
  for (let i = 0; i < key.length; i++) {
    h ^= key.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}

export function demoReleaseNumber(orderId: string): string {
  return String(3_000_000 + (hash(orderId, 'release') % 1_000_000));
}

export function demoPurchaseOrderNumber(orderId: string): string {
  return String(hash(orderId, 'po') % 100_000).padStart(5, '0');
}

export function demoTransferNumbers(orderId: string): string[] {
  const first = 8_200_000 + (hash(orderId, 'transfer') % 100_000);
  const count = 1 + (hash(orderId, 'split') % 3);
  return Array.from({ length: count }, (_, i) => String(first + i));
}

/** Off unless `procurement-demo-order-numbers` is on for this browser. */
export function useDemoOrderNumbers(): boolean {
  return useFeatureFlag('procurement-demo-order-numbers', false).enabled;
}
